import intl from 'react-intl-universal';
import { useState, useEffect, useRef } from 'react';
import { Upload, message, Spin, Popconfirm } from 'antd';
import { default as Button } from '@/components/CssBtn';
import { default as Modal } from '@/components/CssModal';

import {
    DeleteOutlined,
    EyeOutlined,
    LoadingOutlined,
    CloseCircleOutlined,
} from '@ant-design/icons';
import { upload } from '@/utils/fetch';

import { commonRules } from '@/commonConfig/index';

import { fileCDN, isGlobalEnv } from '@/config';
import { ossUrlUpload } from '@/api/common';
import getOSSFileName from '@/utils/getOSSFileName';
import { getFileNameByUrl, getFileType, getUuid } from '@/utils/common';

import { AISupporFileType } from '@/enum/upload';
import { isArray, isString } from 'lodash';
import SessionUpload from './sessionUpload';
// import ZcUpload from './zcUpload';
import H5Upload from './h5Upload';
import FileWarning from './warning';
import Annex from '@/components/Annex';
import type { RcFile, UploadFile, UploadProps } from 'antd/es/upload/interface';
import { ProForm } from '@ant-design/pro-components';
import type { ProFormInstance } from '@ant-design/pro-components';
import type { ModalProps } from 'antd';
import type { UploadListType } from 'antd/es/upload/interface';
import type { FileItem } from './warning';

import sensorLoader from '@/utils/sensorLoader';
import { useRouteData, useModel } from '@umijs/max';

import radioIcon from '@/assets/radio-default-icon.png';
import videoIcon from '@/assets/video-default-icon.png';

import style from '../index.less';

interface DUploadProps extends Omit<UploadProps, 'onChange'> {
    id?: string;
    maxCount?: number;
    maxSize?: number; // 单位 M
    value?: UploadFile[] | string[];
    aiIntercept?: boolean; // 是否需要AI图片相似度对比
    ip_code?: string; // 如果需要进行AI图片相似度对比，需要填入ip_code 就是商品系列code
    onChange?: (files: UploadFile[]) => void;
    isShowH5?: boolean;
    isShowSession?: boolean; // 是否展示会话上传按钮
    onH5Save?: (value: any) => void;
    onH5ModalChange?: (isOpen: boolean) => Promise<any>;
    listType?: UploadListType;
    h5Values?: {
        phone?: string;
        smsType?: number;
        [key: string]: any;
    };
    sessionId?: string;
    readonly?: boolean;
    uploadTips?: string;
}

// const getBase64 = (file: RcFile): Promise<string> =>
//     new Promise((resolve, reject) => {
//         const reader = new FileReader();
//         reader.readAsDataURL(file);
//         reader.onload = () => resolve(reader.result as string);
//         reader.onerror = (error) => reject(error);
//     });
let tsFileList: UploadFile[] = [];
const uploadDir = ['stage', 'prod'].includes(process.env.UMI_ENV)
    ? 'work_order'
    : 'work_order_test';

// const interval = 10 * 60 * 1000;

const DUpload = ({
    id,
    value: originValue,
    onChange,
    h5Values,
    onH5Save,
    onH5ModalChange,
    maxCount = 10,
    maxSize = commonRules.maxFileSize,
    // formRefs,
    accept = 'audio/*, video/* , image/*',
    aiIntercept = false,
    ip_code,
    isShowH5 = false,
    isShowSession = true,
    readonly = false,
    listType = 'picture-card',
    uploadTips = '',
    sessionId,
    ...otherProps
}: DUploadProps) => {
    const currentRoute = useRouteData().route;
    // const [OSSData, setOSSData] = useState<OSSAssetsType>();
    const { OSSData, getOSSAssets, GCSToken, getGCSToken } =
        useModel('formData');
    const [value, setValue] = useState<UploadFile[]>([]);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    // const [isZCModalOpen, setIsZCModalOpen] = useState<boolean>(false);
    const [isH5ModalOpen, setIsH5ModalOpen] = useState<boolean>(false);
    const [selectFileList, setSelectFileList] = useState<any[]>([]);
    const [previewImage, setPreviewImage] = useState<string>('');
    const [previewTitle, setPreviewTitle] = useState<string>('');
    const [disabledNextUpload, setDisabledNextUpload] =
        useState<boolean>(false);

    // 展示附件查重模块
    const [isShowFileWarning, setIsShowFileWarning] = useState<boolean>(false);

    const [form] = ProForm.useForm();
    const H5UploadRef = useRef<ProFormInstance>(form);
    const onCancel = useRef<ModalProps['onCancel']>();
    const onOk = useRef<ModalProps['onOk'][]>([]);
    const delayFileList = useRef<File[] | string[]>([]);
    const setDelayFileList = (fileList: File[]) => {
        delayFileList.current = fileList;
        if (!fileList?.length) {
            setDisabledNextUpload(true);
        } else {
            setDisabledNextUpload(false);
        }
    };

    useEffect(() => {
        if (isGlobalEnv) {
            getGCSToken();
        } else {
            getOSSAssets();
        }

        // 10分钟刷新一次上传认证信息
        // const getOssTask = setInterval(() => {
        //     refreshOSSAssets();
        // }, interval);

        // return () => {
        //     clearInterval(getOssTask);
        // };
    }, []);

    useEffect(() => {
        if (originValue) {
            setValue(
                originValue?.map((file) => {
                    if (isString(file)) {
                        return {
                            name: getFileNameByUrl(file),
                            uid: file,
                            fileName: getFileNameByUrl(file),
                            url: file,
                            status: 'done',
                        };
                    }
                    return file;
                }) || [],
            );
        } else {
            setValue([]);
        }
    }, [originValue]);

    const handleChange: UploadProps['onChange'] = ({ fileList }) => {
        // 多文件同时上传时，由于状态变化太快，可能上次变更的状态下次会覆盖，需要保存上次的状态，merge一次
        const mergeStateFiles = fileList?.map((file, index) => {
            // @ts-ignore
            //  eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { originFileObj, xhr, ...fileData } = file;
            return {
                ...fileData,
                status:
                    tsFileList[index]?.status === 'done' ? 'done' : file.status,
            };
        });

        tsFileList = mergeStateFiles;

        setValue(mergeStateFiles);
        onChange?.([...mergeStateFiles]);
    };

    const getExtraData: UploadProps['data'] = (file) => ({
        key: file.fileName,
        OSSAccessKeyId: OSSData?.ossAccessKeyId,
        policy: OSSData?.policy,
        Signature: OSSData?.signature,
    });

    const beforeUpload: UploadProps['beforeUpload'] = async (
        file: RcFile,
        fileList,
    ) => {
        // onOk.current = [];
        if (!OSSData && !isGlobalEnv) return false;
        if ((value?.length || 0) + fileList.length > 10) {
            message.error(
                intl.get('最多上传{maxCount}个附件，目前只能选择{slot1}个', {
                    maxCount: maxCount,
                    slot1: maxCount - (value?.length || 0),
                }),
            );
            return Upload.LIST_IGNORE;
        }

        if ((value?.length || 0) >= 10) {
            message.error(
                intl.get('最多上传{maxCount}个附件，目前只能选择{slot1}个', {
                    maxCount: maxCount,
                    slot1: maxCount - (value?.length || 0),
                }),
            );
            return Upload.LIST_IGNORE;
        }

        const isGtMaxSize = file.size / 1024 / 1024 > maxSize;
        if (isGtMaxSize) {
            message.error(
                intl.get('最大只能上传{maxSize}M大小的文件', {
                    maxSize: maxSize,
                }),
            );
            return Upload.LIST_IGNORE;
        }

        if (
            aiIntercept &&
            fileList.some((item) => {
                const suffix = item.type?.split('/')[1];
                return AISupporFileType.includes(suffix.toLocaleUpperCase());
            })
        ) {
            setIsShowFileWarning(true);
            return new Promise((resolve) => {
                setDelayFileList(fileList);
                onCancel.current = () => {
                    setIsShowFileWarning(false);
                    resolve(Upload.LIST_IGNORE);
                };

                onOk.current.push(() => {
                    setIsShowFileWarning(false);

                    if (
                        delayFileList.current?.findIndex((delayFile) => {
                            if (typeof delayFile === 'string') {
                                return delayFile === file.name;
                            }
                            return delayFile?.name === file.name;
                        }) > -1
                    ) {
                        //@ts-ignore
                        file.fileName = `${
                            OSSData?.dir || 'image/'
                        }${getOSSFileName(file.name)}`;

                        // @ts-ignore
                        file.url = `${fileCDN}/${file.fileName}`;
                        resolve(file);
                        return;
                    }

                    resolve(Upload.LIST_IGNORE);
                });
            });
        }

        //@ts-ignore
        file.fileName = `${OSSData?.dir || 'image/'}${getOSSFileName(
            file.name,
        )}`;

        // @ts-ignore
        file.url = `${fileCDN}/${file.fileName}`;
        // file.url =
        //     'https://storage.googleapis.com/download/storage/v1/b/service-center-work-order/o/123.jpg?generation=1721719533412690&alt=media';
        return file;
    };

    const onPaste = (event) => {
        let items = event.clipboardData && event.clipboardData.items;
        let originFile = null;
        if (items && items.length) {
            // 检索剪切板items
            for (let i = 0; i < items.length; i++) {
                if (items[i].type.indexOf('image') !== -1) {
                    originFile = items[i].getAsFile();
                    break;
                }
            }
        }

        if (originFile) {
            beforeUpload(originFile, [originFile]).then((file) => {
                if (isString(file)) {
                    // 这里可能是取消
                    return;
                }

                const RCfile = {
                    ...file,
                    type: originFile.type,
                    size: originFile.size,
                };

                upload({
                    method: 'post',
                    action: OSSData?.action,
                    filename: file.fileName,
                    data: getExtraData(file),
                    file,
                    // headers,
                    type: 'formData',
                    withCredentials: false,
                    onProgress: (e: any) => {
                        console.log(e);
                        RCfile.status = 'uploading';
                        handleChange({
                            fileList: [...value, RCfile],
                        });
                    },
                    onSuccess: (ret: any) => {
                        console.log(ret);
                        RCfile.status = 'done';
                        handleChange({
                            fileList: [...value, RCfile],
                        });
                    },
                    onError: (err: any) => {
                        console.log(err);
                    },
                });
            });
        }
    };

    const showSessionModel = (e: any) => {
        e.stopPropagation();
        e.nativeEvent?.stopImmediatePropagation();
        setIsModalOpen(true);

        sensorLoader.reportClick(
            'PopMartCustomerServiceTicketDetailZhiChiUpload',
            {
                page_name: currentRoute.name,
            },
        );
    };
    // const showZCModel = (e: any) => {
    //     e.stopPropagation();
    //     e.nativeEvent?.stopImmediatePropagation();
    //     setIsZCModalOpen(true);

    //     sensorLoader.reportClick(
    //         'PopMartCustomerServiceTicketDetailZhiChiUpload',
    //         {
    //             page_name: currentRoute.name,
    //         },
    //     );
    // };

    const showH5Model = async (e: any) => {
        e.stopPropagation();
        e.nativeEvent?.stopImmediatePropagation();
        setIsH5ModalOpen(true);
        onH5ModalChange?.(true).then((value: any) => {
            if (Object.prototype.toString.call(value) === '[object Object]') {
                setTimeout(() => {
                    // 弹窗创建之后才能拿到ref
                    H5UploadRef.current?.setFieldsValue(value);
                }, 10);
            }
        });
    };
    const onClearH5Message = async (e: any) => {
        e.stopPropagation();
        e.nativeEvent?.stopImmediatePropagation();
        onH5Save && onH5Save(null);
    };

    // const onSaveFiles = () => {
    //     if ((value?.length || 0) + selectFileList.length > 10) {
    //         message.error(
    //             intl.get('最多上传{maxCount}个附件，目前只能选择{slot1}个', {
    //                 maxCount: maxCount,
    //                 slot1: maxCount - (value?.length || 0),
    //             }),
    //         );
    //         return;
    //     }
    //     setIsModalOpen(false);
    //     // setIsZCModalOpen(false);

    //     let isShowFileWarning = false;
    //     const files = (value || []).concat(
    //         selectFileList.map((file) => {
    //             const flieArr = file.split('?')[0].split('.');
    //             const suffix = flieArr[flieArr.length - 1];

    //             if (
    //                 AISupporFileType.includes(suffix.toLocaleUpperCase()) &&
    //                 aiIntercept
    //             ) {
    //                 isShowFileWarning = true;
    //             }

    //             return {
    //                 name: getFileNameByUrl(file),
    //                 uid: file,
    //                 fileName: getFileNameByUrl(file),
    //                 type: `${getFileType(file)}/${suffix}`,
    //                 url: file,
    //                 status: 'done',
    //             };
    //         }),
    //     );

    //     if (isShowFileWarning) {
    //         setDelayFileList(selectFileList);
    //         setIsShowFileWarning(true);

    //         onOk.current = [
    //             () => {
    //                 setDelayFileList([]);
    //                 setIsShowFileWarning(false);
    //                 setSelectFileList([]);
    //                 onChange?.(files);
    //             },
    //         ];

    //         onCancel.current = () => {
    //             setIsShowFileWarning(false);
    //             setDelayFileList([]);
    //             setSelectFileList([]);
    //         };

    //         return;
    //     }

    //     setSelectFileList([]);
    //     onChange?.((value || []).concat(files));
    // };
    const onSaveFilesSession = async () => {
        console.log('onSaveFiles', selectFileList);
        if ((value?.length || 0) + selectFileList.length > 10) {
            message.error(
                intl.get('最多上传{maxCount}个附件，目前只能选择{slot1}个', {
                    maxCount: maxCount,
                    slot1: maxCount - (value?.length || 0),
                }),
            );
            return;
        }
        setIsModalOpen(false);
        // setIsZCModalOpen(false);

        let isShowFileWarning = false;
        let files: any = [];
        await Promise.all(
            selectFileList.map(async (file) => {
                const flieArr = file.split('?')[0].split('.');
                const suffix = flieArr[flieArr.length - 1];

                if (
                    AISupporFileType.includes(suffix.toLocaleUpperCase()) &&
                    aiIntercept
                ) {
                    isShowFileWarning = true;
                }
                try {
                    let params = {
                        dir: uploadDir,
                        fileName: getFileNameByUrl(file),
                        url: file,
                    };
                    const { data } = await ossUrlUpload(params);
                    if (data) {
                        return {
                            name: getFileNameByUrl(data),
                            uid: getUuid(),
                            fileName: getFileNameByUrl(data),
                            type: `${getFileType(data)}/${suffix}`,
                            url: data,
                            status: 'done',
                        };
                    }
                } catch (err) {}
            }),
        )
            .then((res) => {
                files = (value || []).concat(res);
                const selectFileListUrl = res.map((item) => item.url);
                // console.log('selectFileList', res, files, selectFileListUrl);
                if (isShowFileWarning) {
                    setDelayFileList(selectFileListUrl);
                    setIsShowFileWarning(true);

                    onOk.current = [
                        () => {
                            setDelayFileList([]);
                            setIsShowFileWarning(false);
                            setSelectFileList([]);
                            onChange?.(files);
                        },
                    ];

                    onCancel.current = () => {
                        setIsShowFileWarning(false);
                        setDelayFileList([]);
                        setSelectFileList([]);
                    };

                    return;
                }

                setSelectFileList([]);
                onChange?.(files);
            })
            .catch((err) => {
                console.log('err', err);
                message.warning(intl.get('有文件上传失败，请重新上传'));
            });
    };
    const closeModel = () => {
        setIsModalOpen(false);
        setIsH5ModalOpen(false);
        // setIsZCModalOpen(false);
    };

    // const onZcUploadChange = (value: string[]) => {
    //     setSelectFileList(value);
    // };

    const onSessionUploadChange = (value: string[]) => {
        setSelectFileList(value);
    };

    // 点击发送 更改状态
    const onSaveH5 = async () => {
        H5UploadRef.current?.validateFields().then((values: any) => {
            setIsH5ModalOpen(false);
            onH5Save?.(values);
        });
    };

    const handlePreview = async (file: UploadFile) => {
        // if (!file.url && !file.preview) {
        //     file.preview = await getBase64(file.originFileObj as RcFile);
        // }
        setPreviewImage(file.url || (file.preview as string));
        setPreviewTitle(
            file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1),
        );
    };

    const onRemove = (file: UploadFile) => {
        const files: UploadFile[] = (value || []).filter(
            (v) => v.url !== file.url,
        );
        if (onChange) {
            onChange([...files]);
        }
    };

    const customRequest = (options: any) => {
        upload({
            ...options,
            onSuccess: () => {
                handleChange({
                    fileList: tsFileList?.map((file) => {
                        if (file.uid === options.file.uid) {
                            return {
                                ...file,
                                status: 'done',
                            };
                        }
                        return file;
                    }),
                    file: options.file,
                });
            },
        });
    };

    const uploadProps: UploadProps = {
        className: `${style.uploadContainer} ${
            isShowH5 && 'has-h5-contaniner'
        }`,

        name: 'file',
        // defaultFileList: value,
        customRequest: isGlobalEnv ? customRequest : undefined,
        fileList: value,
        action: isGlobalEnv
            ? (file) => {
                  return `https://storage.googleapis.com/upload/storage/v1/b/service-center-work-order/o?uploadType=media&name=${file.fileName}`;
              }
            : OSSData?.action,
        onChange: handleChange,
        onRemove,
        data: isGlobalEnv ? undefined : getExtraData,
        beforeUpload,
        onPreview: handlePreview,
        maxCount,
        headers: isGlobalEnv
            ? {
                  Authorization: `Bearer ${GCSToken}`,
              }
            : undefined,
        accept,
        multiple: true,
        listType,
        disabled: (value?.length || 0) >= maxCount || readonly,

        itemRender: (originNode, file, fileList, actions) => {
            const fileType: string = getFileType(
                file.fileName || file.url || '',
            );

            const onImageLoadError = (e: any) => {
                if (e.target) {
                    e.target.src = file.url;
                }
            };

            return (
                <div
                    className={`ant-upload-list-item ant-upload-list-item-success ${
                        style.uploadItemContainer
                    } ${style['uploadItemContainer_' + listType]}`}
                >
                    {file.status === 'uploading' && (
                        <Spin
                            indicator={
                                <LoadingOutlined
                                    style={{ fontSize: 24 }}
                                    spin
                                />
                            }
                        />
                    )}
                    {file.status === 'done' && (
                        <div className={style.fileItemContainer}>
                            {fileType === 'video' && (
                                <video
                                    className={style.uploadVideoFile}
                                    poster={videoIcon}
                                    src={file.url}
                                ></video>
                            )}
                            {fileType === 'image' && (
                                <img
                                    className={style.uploadVideoFile}
                                    src={file.url}
                                    onError={onImageLoadError}
                                />
                            )}
                            {fileType === 'radio' && (
                                <img
                                    className={style.uploadVideoFile}
                                    src={radioIcon}
                                />
                            )}
                            {!['image', 'video', 'radio'].includes(
                                fileType,
                            ) && (
                                <span className={style.uploadVideoFile}>
                                    {file.name}
                                </span>
                            )}
                        </div>
                    )}

                    {file.status === 'error' && (
                        <div style={{ color: 'red' }}>
                            {intl.get('上传失败！')}
                        </div>
                    )}

                    <span className="ant-upload-list-item-actions">
                        {file.status !== 'error' &&
                            ['image', 'video', 'radio'].includes(fileType) && (
                                <EyeOutlined onClick={actions.preview} />
                            )}

                        {!readonly && (
                            <Popconfirm
                                title={intl.get('确定要删除这个附件吗？')}
                                onConfirm={actions.remove}
                            >
                                <DeleteOutlined />
                            </Popconfirm>
                        )}
                    </span>
                </div>
            );
        },
        ...otherProps,
    };

    const onNextUploadFileChange = (files: FileItem[]) => {
        setDelayFileList(files?.map((file) => file.file));
    };

    const onNextUpload = () => {
        if (delayFileList?.current?.length) {
            if (isArray(onOk.current)) {
                // @ts-ignore
                onOk.current.forEach((cb) => cb?.());
                onOk.current = [];
            }
        } else {
            message.warning(intl.get('暂无可需要上传的附件'));
            setIsShowFileWarning(false);
        }
    };

    return (
        <div
            id={id}
            className={style.uploadWarpper}
            onPaste={onPaste}
            tabIndex={100}
        >
            {(!readonly || (readonly && !!value.length)) && (
                <Upload {...uploadProps}>
                    {(value?.length || 0) < maxCount && !readonly && (
                        <div>
                            {(value?.length || 0) < maxCount && (
                                <Button
                                    className={style.btnBlock}
                                    disabled={
                                        (value?.length || 0) >= maxCount ||
                                        readonly
                                    }
                                    type={
                                        ['picture-card', 'picture'].includes(
                                            listType,
                                        )
                                            ? 'link'
                                            : 'default'
                                    }
                                    onClick={() => {
                                        sensorLoader.reportClick(
                                            'PopMartCustomerServiceTicketDetailNormallyUpload',
                                            {
                                                page_name: currentRoute.name,
                                            },
                                        );
                                    }}
                                >
                                    {intl.get('普通上传')}
                                </Button>
                            )}
                            {/* {(value?.length || 0) < maxCount &&
                                isShowSession && (
                                    <Button
                                        className={style.btnBlock}
                                        disabled={
                                            (value?.length || 0) >= maxCount ||
                                            readonly
                                        }
                                        type={
                                            [
                                                'picture-card',
                                                'picture',
                                            ].includes(listType)
                                                ? 'link'
                                                : 'default'
                                        }
                                        onClick={showZCModel}
                                    >
                                        {intl.get('智齿上传')}
                                    </Button>
                                )} */}
                            {(value?.length || 0) < maxCount &&
                                isShowSession &&
                                !isGlobalEnv && ( // 海外下掉会话上传
                                    <Button
                                        className={style.btnBlock}
                                        disabled={
                                            (value?.length || 0) >= maxCount ||
                                            readonly
                                        }
                                        type={
                                            [
                                                'picture-card',
                                                'picture',
                                            ].includes(listType)
                                                ? 'link'
                                                : 'default'
                                        }
                                        onClick={showSessionModel}
                                    >
                                        {intl.get('会话上传')}
                                    </Button>
                                )}

                            <div>
                                {isShowH5 && (
                                    <div className={style.h5BtnContainer}>
                                        <Button
                                            type={
                                                [
                                                    'picture-card',
                                                    'picture',
                                                ].includes(listType)
                                                    ? 'link'
                                                    : 'default'
                                            }
                                            onClick={showH5Model}
                                        >
                                            {intl.get('H5上传')}
                                        </Button>

                                        {h5Values?.phone && (
                                            <span className={style.h5State}>
                                                {intl.get('待发送')}

                                                <CloseCircleOutlined
                                                    onClick={onClearH5Message}
                                                    className={style.clearBtn}
                                                />
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </Upload>
            )}

            {readonly && !value.length && '-'}
            {!readonly &&
                accept.indexOf('image') > 0 &&
                intl.get('点击此区域后，可使用 【Ctrl + V】 进行粘贴上传图片')}

            {uploadTips && <div className={style.uploadTips}>{uploadTips}</div>}
            <Modal
                title={intl.get('会话上传')}
                width={800}
                open={isModalOpen}
                onOk={onSaveFilesSession}
                onCancel={closeModel}
                destroyOnClose={true}
            >
                <SessionUpload
                    sessionId={sessionId}
                    value={selectFileList}
                    onChange={onSessionUploadChange}
                ></SessionUpload>
            </Modal>

            {/* <Modal
                title={intl.get('智齿上传')}
                width={800}
                open={isZCModalOpen}
                onOk={onSaveFiles}
                onCancel={closeModel}
                destroyOnClose={true}
            >
                <ZcUpload
                    value={selectFileList}
                    onChange={onZcUploadChange}
                ></ZcUpload>
            </Modal> */}

            <Modal
                title={intl.get('触发短信')}
                width={800}
                open={isH5ModalOpen}
                onOk={onSaveH5}
                onCancel={closeModel}
                destroyOnClose={true}
                okText={intl.get('保存')}
            >
                <H5Upload
                    form={form}
                    ref={H5UploadRef}
                    initialValues={h5Values}
                ></H5Upload>
            </Modal>

            <Modal
                open={isShowFileWarning}
                title={intl.get('附件异常提醒')}
                width={800}
                destroyOnClose={true}
                okText={intl.get('继续上传')}
                onCancel={onCancel.current}
                onOk={onNextUpload}
                okButtonProps={{
                    disabled: disabledNextUpload,
                }}
            >
                <FileWarning
                    ip_code={ip_code}
                    fileList={delayFileList.current}
                    onChange={onNextUploadFileChange}
                ></FileWarning>
            </Modal>

            <Annex
                src={previewImage}
                title={previewTitle}
                removeUrl={(val: string) => setPreviewImage(val)}
            ></Annex>
        </div>
    );
};

export default DUpload;
